import React, { useState, useEffect, useCallback } from 'react';
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { createStudy, launchStudy, getStudy, getStudyReport } from '../../api/research';
import { analytics } from '../../services/analytics';

// ── Types ─────────────────────────────────────────────────────────────────

type View = 'creator' | 'plan' | 'live' | 'report';

type Intent =
  | 'Product Validation'
  | 'Pricing Sensitivity'
  | 'Competitor Perception'
  | 'Feature Discovery'
  | 'Satisfaction'
  | 'Custom';

interface StudyDraft {
  intent: Intent | '';
  title: string;
  hypotheses: string;
  targetProfile: string;
  contactsRaw: string;
}

interface StudyData {
  id: string;
  title: string;
  intent: string;
  status: string;
  questions: string[];
  script: string;
  estimatedDuration: number;
  total_contacts: number;
  calls_completed: number;
  calls_failed: number;
}

interface Contact {
  id: string;
  phone: string;
  status: string;
}

interface HypothesisValidation {
  hypothesis: string;
  verdict: 'confirmed' | 'rejected' | 'partial';
  evidence: string;
}

interface Recommendation {
  priority: 'high' | 'medium' | 'low';
  recommendation: string;
}

interface TopTheme {
  theme: string;
  count: number;
  quotes: string[];
}

interface Report {
  executive_summary?: string;
  executiveSummary?: string;
  key_findings?: string[];
  keyFindings?: string[];
  sentiment_breakdown?: Record<string, number>;
  sentimentBreakdown?: Record<string, number>;
  top_themes?: TopTheme[];
  topThemes?: TopTheme[];
  hypothesis_validation?: HypothesisValidation[];
  hypothesisValidation?: HypothesisValidation[];
  recommendations?: Recommendation[];
  notable_quotes?: string[];
  notableQuotes?: string[];
  surprises?: string[];
}

// ── Helpers ──────────────────────────────────────────────────────────────

const INTENT_CARDS: { label: Intent; icon: string; desc: string }[] = [
  { label: 'Product Validation', icon: '🎯', desc: 'Test if your product solves a real problem' },
  { label: 'Pricing Sensitivity', icon: '💰', desc: "Discover what people will pay and why" },
  { label: 'Competitor Perception', icon: '🔭', desc: 'Understand how you compare in their minds' },
  { label: 'Feature Discovery', icon: '🔍', desc: 'Find the features that actually matter' },
  { label: 'Satisfaction', icon: '⭐', desc: 'Measure and improve customer happiness' },
  { label: 'Custom', icon: '✏️', desc: 'Define your own research objectives' },
];

const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#10b981',
  negative: '#ef4444',
  neutral: '#6b7280',
  mixed: '#f59e0b',
};

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; color: string }> = {
    pending: { bg: 'rgba(107,114,128,0.2)', color: '#9ca3af' },
    calling: { bg: 'rgba(99,102,241,0.15)', color: '#818cf8' },
    completed: { bg: 'rgba(16,185,129,0.15)', color: '#34d399' },
    failed: { bg: 'rgba(239,68,68,0.15)', color: '#f87171' },
  };
  const c = colors[status] || colors['pending']!;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '2px 8px', borderRadius: 9999,
        fontSize: 11, fontWeight: 600,
        background: c.bg, color: c.color,
      }}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function VerdictBadge({ verdict }: { verdict: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    confirmed: { bg: 'rgba(16,185,129,0.15)', color: '#34d399', label: '✓ Confirmed' },
    rejected: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', label: '✗ Rejected' },
    partial: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', label: '~ Partial' },
  };
  const v = map[verdict] || map['partial']!;
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, background: v.bg, color: v.color }}>
      {v.label}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const map: Record<string, { color: string }> = {
    high: { color: '#f87171' },
    medium: { color: '#fbbf24' },
    low: { color: '#34d399' },
  };
  const c = map[priority] || map['medium']!;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: 1 }}>
      {priority}
    </span>
  );
}

// ── Main Component ────────────────────────────────────────────────────────

export const ResearchStudio: React.FC = () => {
  const [view, setView] = useState<View>('creator');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draft state
  const [draft, setDraft] = useState<StudyDraft>({
    intent: '',
    title: '',
    hypotheses: '',
    targetProfile: '',
    contactsRaw: '',
  });

  // Study data after creation
  const [study, setStudy] = useState<StudyData | null>(null);
  const [editableQuestions, setEditableQuestions] = useState<string[]>([]);

  // Live dashboard
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [topThemes, setTopThemes] = useState<{ theme: string; count: number }[]>([]);
  const [pollInterval, setPollInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  // Report
  const [report, setReport] = useState<Report | null>(null);
  const [expandedFindings, setExpandedFindings] = useState<Set<number>>(new Set());

  // ── Poll live data ────────────────────────────────────────────────────

  const pollStudy = useCallback(async () => {
    if (!study?.id) return;
    try {
      const data = await getStudy(study.id);
      setStudy((s) => s ? { ...s, ...data.study } : data.study);
      setContacts(data.contacts || []);
      setTopThemes(data.topThemes || []);
    } catch {}
  }, [study?.id]);

  useEffect(() => {
    if (view === 'live' && study?.id) {
      const iv = setInterval(pollStudy, 3000);
      setPollInterval(iv);
      return () => clearInterval(iv);
    }
    if (pollInterval) { clearInterval(pollInterval); setPollInterval(null); }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, study?.id]);

  // ── Step 1: Generate plan ──────────────────────────────────────────────

  const handleGeneratePlan = async () => {
    if (!draft.intent || !draft.title) {
      setError('Please select a research intent and enter a title.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const hypotheses = draft.hypotheses
        .split('\n')
        .map((h) => h.trim())
        .filter(Boolean);
      const contacts = draft.contactsRaw
        .split('\n')
        .map((p) => p.trim())
        .filter(Boolean);

      const result = await createStudy({
        title: draft.title,
        intent: draft.intent,
        hypotheses,
        targetProfile: draft.targetProfile,
        contacts,
      });

      analytics.track('research_study_created', { intent: draft.intent, studyId: result.study?.id });
      setStudy(result.study);
      setEditableQuestions(result.study.questions || []);
      setView('plan');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to generate research plan.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Launch calls ───────────────────────────────────────────────

  const handleLaunchCalls = async () => {
    if (!study?.id) return;
    setLoading(true);
    setError(null);
    try {
      await launchStudy(study.id);
      analytics.track('research_study_launched', { studyId: study.id, contactCount: (study as any).contacts?.length });
      await pollStudy();
      setView('live');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to launch calls.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 4: Load report ────────────────────────────────────────────────

  const handleViewReport = async () => {
    if (!study?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getStudyReport(study.id);
      analytics.track('research_report_viewed', { studyId: study.id });
      setReport(data.report);
      setView('report');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportMarkdown = () => {
    if (!report || !study) return;
    const findings = (report.key_findings || report.keyFindings || []).map((f) => `- ${f}`).join('\n');
    const hypotheses = (report.hypothesis_validation || report.hypothesisValidation || [])
      .map((h) => `### ${h.hypothesis}\n**Verdict:** ${h.verdict}\n${h.evidence}`)
      .join('\n\n');
    const recs = (report.recommendations || [])
      .map((r) => `- [${r.priority.toUpperCase()}] ${r.recommendation}`)
      .join('\n');
    const quotes = (report.notable_quotes || report.notableQuotes || [])
      .map((q) => `> "${q}"`)
      .join('\n\n');

    const md = `# ${study.title} — Research Report

## Executive Summary
${report.executive_summary || report.executiveSummary || ''}

## Key Findings
${findings}

## Hypothesis Validation
${hypotheses}

## Recommendations
${recs}

## Notable Quotes
${quotes}
`;
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${study.title.replace(/\s+/g, '-')}-research-report.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Sentiment chart data ───────────────────────────────────────────────

  const sentimentData = (() => {
    const breakdown = report?.sentiment_breakdown || report?.sentimentBreakdown || {};
    return Object.entries(breakdown)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));
  })();

  // ── Render ────────────────────────────────────────────────────────────

  const card = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8,
    color: '#f3f4f6',
    padding: '10px 12px',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const btnPrimary: React.CSSProperties = {
    background: 'rgba(99,102,241,0.85)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  };

  const btnSecondary: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    color: '#9ca3af',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    padding: '8px 18px',
    fontSize: 13,
    cursor: 'pointer',
  };

  // ── Step indicators ───────────────────────────────────────────────────

  const steps = [
    { key: 'creator', label: 'Design', icon: '🧪' },
    { key: 'plan', label: 'Review', icon: '📋' },
    { key: 'live', label: 'Live', icon: '📡' },
    { key: 'report', label: 'Report', icon: '📊' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0f1117', color: '#f3f4f6', padding: '24px 32px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#fff', margin: 0 }}>🧪 Research Studio</h1>
        <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>
          AI-powered voice market research — real phone interviews, real insights
        </p>
      </div>

      {/* Step nav */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
        {steps.map((s, i) => {
          const isActive = s.key === view;
          const isDone = steps.findIndex((x) => x.key === view) > i;
          return (
            <div
              key={s.key}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 14px', borderRadius: 9999, fontSize: 13, fontWeight: 600,
                background: isActive ? 'rgba(99,102,241,0.2)' : 'transparent',
                color: isActive ? '#818cf8' : isDone ? '#34d399' : '#4b5563',
                border: isActive ? '1px solid rgba(99,102,241,0.35)' : '1px solid transparent',
                cursor: isDone ? 'pointer' : 'default',
              }}
              onClick={() => { if (isDone) setView(s.key as View); }}
            >
              {s.icon} {s.label}
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 16px', color: '#f87171', fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {/* ── VIEW 1: Study Creator ────────────────────────────────────────── */}
      {view === 'creator' && (
        <div>
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: '0 0 16px' }}>Research Intent</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {INTENT_CARDS.map((ic) => {
                const selected = draft.intent === ic.label;
                return (
                  <button
                    key={ic.label}
                    onClick={() => setDraft((d) => ({ ...d, intent: ic.label }))}
                    style={{
                      background: selected ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                      border: selected ? '1px solid rgba(99,102,241,0.4)' : '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 10, padding: '14px 12px', cursor: 'pointer', textAlign: 'left',
                      color: selected ? '#a5b4fc' : '#9ca3af',
                    }}
                  >
                    <div style={{ fontSize: 20, marginBottom: 6 }}>{ic.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: selected ? '#e0e7ff' : '#d1d5db', marginBottom: 4 }}>{ic.label}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{ic.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: '0 0 14px' }}>Study Details</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Study Title</label>
              <input
                style={inputStyle}
                placeholder="e.g. Q2 2026 Pricing Research"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Hypotheses</label>
              <textarea
                style={{ ...inputStyle, minHeight: 100, resize: 'vertical' } as React.CSSProperties}
                placeholder={"What do you think you know? One hypothesis per line.\ne.g. Customers churn because pricing is confusing\nUsers want a mobile app above all else"}
                value={draft.hypotheses}
                onChange={(e) => setDraft((d) => ({ ...d, hypotheses: e.target.value }))}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Target Profile</label>
              <input
                style={inputStyle}
                placeholder="e.g. SaaS founders who tried our product in the last 90 days"
                value={draft.targetProfile}
                onChange={(e) => setDraft((d) => ({ ...d, targetProfile: e.target.value }))}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Contact Phone Numbers</label>
              <textarea
                style={{ ...inputStyle, minHeight: 80, fontFamily: 'monospace', resize: 'vertical' } as React.CSSProperties}
                placeholder={"+14155551234\n+14155555678\n+818088474463"}
                value={draft.contactsRaw}
                onChange={(e) => setDraft((d) => ({ ...d, contactsRaw: e.target.value }))}
              />
              <p style={{ fontSize: 11, color: '#4b5563', margin: '4px 0 0' }}>
                E.164 format, one number per line
              </p>
            </div>
          </div>

          <button
            style={{ ...btnPrimary, opacity: loading ? 0.7 : 1 }}
            onClick={handleGeneratePlan}
            disabled={loading}
          >
            {loading ? '⏳ Generating…' : '✨ Generate Research Plan'}
          </button>
        </div>
      )}

      {/* ── VIEW 2: Plan Preview ─────────────────────────────────────────── */}
      {view === 'plan' && study && (
        <div>
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: 0 }}>AI-Generated Questions</h3>
              <span style={{ fontSize: 12, color: '#6b7280' }}>~{study.estimatedDuration} min call</span>
            </div>
            {editableQuestions.map((q, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#818cf8', minWidth: 22, paddingTop: 8 }}>{i + 1}.</span>
                <textarea
                  style={{ ...inputStyle, flex: 1, minHeight: 60, resize: 'vertical' } as React.CSSProperties}
                  value={q}
                  onChange={(e) => {
                    const updated = [...editableQuestions];
                    updated[i] = e.target.value;
                    setEditableQuestions(updated);
                  }}
                />
                <button
                  onClick={() => setEditableQuestions((qs) => qs.filter((_, idx) => idx !== i))}
                  style={{ ...btnSecondary, padding: '8px 12px', color: '#f87171' }}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              onClick={() => setEditableQuestions((qs) => [...qs, ''])}
              style={{ ...btnSecondary, marginTop: 4, fontSize: 12 }}
            >
              + Add question
            </button>
          </div>

          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: '0 0 12px' }}>Script Preview</h3>
            <pre style={{ fontSize: 13, color: '#9ca3af', whiteSpace: 'pre-wrap', lineHeight: 1.7, margin: 0 }}>
              {study.script}
            </pre>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button style={btnSecondary} onClick={() => setView('creator')}>← Back</button>
            <button
              style={{ ...btnPrimary, background: 'rgba(16,185,129,0.8)', opacity: loading ? 0.7 : 1 }}
              onClick={handleLaunchCalls}
              disabled={loading}
            >
              {loading ? '⏳ Launching…' : '📞 Launch Research Calls'}
            </button>
          </div>
        </div>
      )}

      {/* ── VIEW 3: Live Dashboard ────────────────────────────────────────── */}
      {view === 'live' && study && (
        <div>
          {/* Progress */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: 0 }}>Call Progress</h3>
              <span style={{ fontSize: 13, color: '#818cf8', fontWeight: 600 }}>
                {study.calls_completed} / {study.total_contacts} completed
              </span>
            </div>
            <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
              <div style={{
                height: '100%',
                width: `${study.total_contacts > 0 ? (study.calls_completed / study.total_contacts) * 100 : 0}%`,
                background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                borderRadius: 4,
                transition: 'width 0.5s ease',
              }} />
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#6b7280' }}>
              <span>✅ {study.calls_completed} completed</span>
              <span>❌ {study.calls_failed} failed</span>
              <span>⏳ {study.total_contacts - study.calls_completed - study.calls_failed} pending</span>
            </div>
          </div>

          {/* Early themes */}
          {topThemes.length > 0 && (
            <div style={card}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#e5e7eb', margin: '0 0 12px' }}>🔍 Emerging Themes</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {topThemes.map((t) => (
                  <span key={t.theme} style={{
                    background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)',
                    borderRadius: 9999, padding: '4px 12px', fontSize: 12, color: '#a5b4fc',
                  }}>
                    {t.theme} <span style={{ opacity: 0.6 }}>×{t.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Call feed */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: '0 0 14px' }}>Live Call Feed</h3>
            {contacts.length === 0 ? (
              <p style={{ color: '#4b5563', fontSize: 13 }}>No contacts yet…</p>
            ) : (
              contacts.map((c) => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize: 13, fontFamily: 'monospace', color: '#d1d5db' }}>{c.phone}</span>
                  <StatusBadge status={c.status} />
                </div>
              ))
            )}
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button style={btnSecondary} onClick={pollStudy}>↻ Refresh</button>
            <button
              style={{ ...btnPrimary, opacity: loading ? 0.7 : 1 }}
              onClick={handleViewReport}
              disabled={loading}
            >
              {loading ? '⏳ Generating…' : '📊 View Report'}
            </button>
          </div>
        </div>
      )}

      {/* ── VIEW 4: Research Report ───────────────────────────────────────── */}
      {view === 'report' && report && study && (
        <div>
          {/* Executive Summary */}
          <div style={{ ...card, borderColor: 'rgba(99,102,241,0.25)' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#a5b4fc', margin: '0 0 12px' }}>📝 Executive Summary</h3>
            <p style={{ fontSize: 14, color: '#d1d5db', lineHeight: 1.75, margin: 0 }}>
              {report.executive_summary || report.executiveSummary}
            </p>
          </div>

          {/* Sentiment + Key Findings row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            {/* Sentiment donut */}
            <div style={{ ...card, margin: 0 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#e5e7eb', margin: '0 0 12px' }}>Sentiment</h3>
              {sentimentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={sentimentData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                      {sentimentData.map((entry) => (
                        <Cell key={entry.name} fill={SENTIMENT_COLORS[entry.name.toLowerCase()] || '#818cf8'} />
                      ))}
                    </Pie>
                    <RechartsTooltip contentStyle={{ background: '#1f2937', border: 'none', borderRadius: 8, color: '#f3f4f6', fontSize: 12 }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: '#9ca3af' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p style={{ color: '#4b5563', fontSize: 12 }}>No sentiment data</p>
              )}
            </div>

            {/* Surprises */}
            <div style={{ ...card, margin: 0 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fbbf24', margin: '0 0 12px' }}>⚡ Surprises</h3>
              {(report.surprises || []).length > 0 ? (
                (report.surprises || []).map((s, i) => (
                  <p key={i} style={{ fontSize: 13, color: '#d1d5db', margin: '0 0 8px', lineHeight: 1.5 }}>• {s}</p>
                ))
              ) : (
                <p style={{ color: '#4b5563', fontSize: 12 }}>No surprises detected</p>
              )}
            </div>
          </div>

          {/* Hypothesis Validation */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: '0 0 14px' }}>🔬 Hypothesis Validation</h3>
            {(report.hypothesis_validation || report.hypothesisValidation || []).map((h, i) => (
              <div key={i} style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, marginBottom: 10, border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#d1d5db', margin: 0, flex: 1, paddingRight: 12 }}>{h.hypothesis}</p>
                  <VerdictBadge verdict={h.verdict} />
                </div>
                <p style={{ fontSize: 12, color: '#9ca3af', margin: 0, lineHeight: 1.6 }}>{h.evidence}</p>
              </div>
            ))}
          </div>

          {/* Key Findings */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: '0 0 14px' }}>🔑 Key Findings</h3>
            {(report.key_findings || report.keyFindings || []).map((f, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <button
                  onClick={() => setExpandedFindings((prev) => {
                    const next = new Set(prev);
                    next.has(i) ? next.delete(i) : next.add(i);
                    return next;
                  })}
                  style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: 13, textAlign: 'left', padding: '6px 0', display: 'flex', gap: 8 }}
                >
                  <span style={{ color: '#818cf8' }}>{expandedFindings.has(i) ? '▼' : '▶'}</span>
                  {f}
                </button>
              </div>
            ))}
          </div>

          {/* Top Themes */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: '0 0 14px' }}>💡 Top Themes</h3>
            {(report.top_themes || report.topThemes || []).map((t, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#a5b4fc' }}>{t.theme}</span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>mentioned {t.count}×</span>
                </div>
                {(t.quotes || []).slice(0, 2).map((q, j) => (
                  <p key={j} style={{ fontSize: 12, color: '#6b7280', margin: '0 0 4px', paddingLeft: 12, borderLeft: '2px solid rgba(99,102,241,0.3)', fontStyle: 'italic' }}>
                    "{q}"
                  </p>
                ))}
              </div>
            ))}
          </div>

          {/* Recommendations */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: '0 0 14px' }}>📌 Recommendations</h3>
            {(report.recommendations || []).map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <PriorityBadge priority={r.priority} />
                <span style={{ fontSize: 13, color: '#d1d5db', lineHeight: 1.5 }}>{r.recommendation}</span>
              </div>
            ))}
          </div>

          {/* Notable Quotes */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#e5e7eb', margin: '0 0 14px' }}>💬 Notable Quotes</h3>
            {(report.notable_quotes || report.notableQuotes || []).map((q, i) => (
              <blockquote key={i} style={{
                margin: '0 0 16px',
                padding: '12px 16px',
                borderLeft: '3px solid rgba(99,102,241,0.5)',
                background: 'rgba(99,102,241,0.05)',
                borderRadius: '0 8px 8px 0',
              }}>
                <p style={{ fontSize: 16, fontStyle: 'italic', color: '#e5e7eb', margin: 0, lineHeight: 1.6 }}>"{q}"</p>
              </blockquote>
            ))}
          </div>

          {/* Export */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button style={btnSecondary} onClick={() => setView('live')}>← Back to Dashboard</button>
            <button style={btnPrimary} onClick={handleExportMarkdown}>
              ⬇ Export Markdown
            </button>
            <button
              style={{ ...btnSecondary, color: '#818cf8' }}
              onClick={() => { setLoading(true); getStudyReport(study.id, true).then((d) => { setReport(d.report); setLoading(false); }).catch(() => setLoading(false)); }}
            >
              ↻ Regenerate
            </button>
          </div>
        </div>
      )}

      {/* Loading overlay while navigating */}
      {loading && view !== 'creator' && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 14 }}>
          ⏳ AI is working on it…
        </div>
      )}
    </div>
  );
};

export default ResearchStudio;
